import { action, computed, makeAutoObservable, observable, runInAction } from "mobx";

import type { PresetFile } from "../skill-presets";
import type { EditorTreeNode } from "../types";
import {
  buildPathWithinParent,
  getParentPath,
  getPathDepth,
  isMarkdownSkillFilePath,
  getTreeItemNameError,
  normalizeTreeItemName,
  normalizePresetFiles,
  getPathChangeReviewKind,
  normalizeRelativePath,
} from "../utils/path-utils";
import {
  documentJsonToMarkdown,
  markdownToDocumentJson,
  type SkillDocumentJson,
} from "../utils/skill-markdown-convert";
import { buildEditorTree } from "../utils/tree-utils";

import type { SkillCollectionReviewItem } from "./skill-collection-editor-types";

import type { FileMove, FileUpdate } from "@/queries/skill-collections";

export class SkillCollectionEditorStore {
  @observable private _selectedPath: string | null = null;
  @observable private _contentByPath = new Map<string, string>();
  @observable private _originalContentByPath = new Map<string, string>();
  @observable private _editorDocByPath = new Map<string, SkillDocumentJson>();
  @observable private _originalDocSnapshotByPath = new Map<string, string>();
  @observable private _filePaths = new Set<string>();
  @observable private _addedFolderPaths = new Set<string>();
  @observable private _addedFilePaths = new Set<string>();
  @observable private _renamedPaths = new Map<string, string>();
  @observable private _deletedPaths = new Set<string>();
  @observable private _hasImportedContent = false;
  private _importProtectedPaths = new Set<string>();
  @observable private _editorBaselinedPaths = new Set<string>();
  @observable private _serverFilePaths = new Set<string>();
  @observable private _serverFolderPaths = new Set<string>();

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  @computed get selectedPath() {
    return this._selectedPath;
  }

  @computed get contentByPath() {
    return this._contentByPath;
  }

  @computed get serverFilePaths() {
    return this._serverFilePaths;
  }

  @computed get filePaths(): ReadonlySet<string> {
    return this._filePaths;
  }

  @computed get isEmpty() {
    return this._filePaths.size === 0;
  }

  getDocumentJson(path: string): SkillDocumentJson | undefined {
    return this._editorDocByPath.get(path);
  }

  @computed get hasImportedContent() {
    return this._hasImportedContent;
  }

  @action clearImportFlag() {
    this._hasImportedContent = false;
  }

  @computed get dirtyFilePaths() {
    const dirty = new Set<string>();

    this._addedFilePaths.forEach(path => dirty.add(path));

    this._renamedPaths.forEach((newPath, oldPath) => {
      if (oldPath !== newPath) {
        dirty.add(newPath);
      }
    });

    this._contentByPath.forEach((_content, path) => {
      if (this._addedFilePaths.has(path)) {
        return;
      }

      if (isMarkdownSkillFilePath(path)) {
        if (this._originalDocSnapshotByPath.has(path) && this.isMarkdownDirty(path)) {
          dirty.add(path);
        } else if (!this._originalDocSnapshotByPath.has(path) && this._serverFilePaths.has(path)) {
          dirty.add(path);
        }
        return;
      }

      if (this._originalContentByPath.has(path)) {
        const originalContent = this._originalContentByPath.get(path) ?? "";
        if (originalContent !== _content) {
          dirty.add(path);
        }
        return;
      }

      if (this._serverFilePaths.has(path)) {
        dirty.add(path);
      }
    });

    return dirty;
  }

  @computed get isDirty() {
    const hasNewEmptyFolders = this.emptyFolderPaths.some(
      path => !this._serverFolderPaths.has(path),
    );
    const hasPendingRenames = [...this._renamedPaths].some(
      ([oldPath, newPath]) => oldPath !== newPath,
    );
    return (
      this.dirtyFilePaths.size > 0 ||
      hasNewEmptyFolders ||
      hasPendingRenames ||
      this._deletedPaths.size > 0
    );
  }

  @computed get hasUnsavedStructureChanges() {
    const hasPendingRenames = [...this._renamedPaths].some(
      ([oldPath, newPath]) => oldPath !== newPath,
    );
    if (this._addedFilePaths.size > 0 || hasPendingRenames || this._deletedPaths.size > 0) {
      return true;
    }

    for (const path of this._addedFolderPaths) {
      if (!this._serverFolderPaths.has(path)) {
        return true;
      }
    }
    return false;
  }

  @computed get allFolderPaths() {
    const folderSet = new Set<string>();

    for (const path of this._serverFolderPaths) {
      if (!this.isPathDeleted(path)) {
        folderSet.add(path);
      }
    }

    for (const path of this._filePaths) {
      if (this.isPathDeleted(path)) {
        continue;
      }
      const parts = path.split("/").filter(Boolean);
      for (let index = 0; index < parts.length - 1; index += 1) {
        folderSet.add(parts.slice(0, index + 1).join("/"));
      }
    }

    for (const path of this._addedFolderPaths) {
      if (!this.isPathDeleted(path)) {
        folderSet.add(path);
      }
    }

    return [...folderSet];
  }

  @computed get allFolderPathSet() {
    return new Set(this.allFolderPaths);
  }

  @computed get emptyFolderKeepFiles(): FileUpdate[] {
    const occupiedFolders = new Set<string>();

    for (const path of this._filePaths) {
      const parts = path.split("/").filter(Boolean);
      for (let index = 0; index < parts.length - 1; index += 1) {
        occupiedFolders.add(parts.slice(0, index + 1).join("/"));
      }
    }

    return [...this._addedFolderPaths]
      .filter(folderPath => !occupiedFolders.has(folderPath))
      .map(folderPath => ({
        path: `${folderPath}/.keep.md`,
        content: "",
      }));
  }

  @computed get emptyFolderPaths(): string[] {
    return this.emptyFolderKeepFiles.map(f => f.path.replace(/\/.keep\.md$/, ""));
  }

  @computed get displayTree(): EditorTreeNode[] {
    return buildEditorTree([...this._filePaths], this.allFolderPaths);
  }

  @computed get pendingPathMoves(): FileMove[] {
    return [...this._renamedPaths]
      .filter(([oldPath, newPath]) => oldPath !== newPath)
      .map(([oldPath, newPath]) => ({
        oldPath,
        newPath,
      }));
  }

  @computed get savePayload(): { fileUpdates: FileUpdate[]; moves: FileMove[]; deletes: string[] } {
    const fileUpdates: FileUpdate[] = [
      ...[...this.dirtyFilePaths].map(path => ({
        path,
        content: this._contentByPath.get(path) ?? "",
      })),
      ...this.emptyFolderKeepFiles,
    ];

    return { fileUpdates, moves: this.pendingPathMoves, deletes: [...this._deletedPaths] };
  }

  @computed private get deletedFilePaths(): Set<string> {
    const result = new Set<string>();
    for (const filePath of this._serverFilePaths) {
      for (const deletedPath of this._deletedPaths) {
        if (filePath === deletedPath || filePath.startsWith(`${deletedPath}/`)) {
          result.add(filePath);
          break;
        }
      }
    }
    return result;
  }

  @computed private get movedTargetToSource(): Map<string, string> {
    const result = new Map<string, string>();
    for (const [oldPath, newPath] of this._renamedPaths) {
      result.set(newPath, oldPath);
    }
    return result;
  }

  @computed private get addedReviewItems(): SkillCollectionReviewItem[] {
    return [...this._addedFilePaths]
      .sort((a, b) => a.localeCompare(b))
      .map(path => ({
        key: `added:${path}`,
        changeType: "added" as const,
        path,
        currentContent: this._contentByPath.get(path) ?? "",
        requiresOriginalFetch: false,
      }));
  }

  @computed private get renamedReviewItems(): SkillCollectionReviewItem[] {
    return [...this._renamedPaths]
      .filter(([oldPath, newPath]) => oldPath !== newPath)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([oldPath, newPath]) => {
        const changeType = getPathChangeReviewKind(oldPath, newPath);
        const originalContent =
          this.getReviewOriginalMarkdown(newPath) ?? this.getReviewOriginalMarkdown(oldPath);

        return {
          key: `${changeType}:${oldPath}:${newPath}`,
          changeType,
          path: newPath,
          oldPath,
          newPath,
          originalContent,
          currentContent: this._contentByPath.get(newPath) ?? "",
          requiresOriginalFetch: originalContent === undefined,
        };
      });
  }

  @computed private get modifiedReviewItems(): SkillCollectionReviewItem[] {
    return [...this.dirtyFilePaths]
      .filter(
        path =>
          !this._addedFilePaths.has(path) &&
          !this.movedTargetToSource.has(path) &&
          !this.deletedFilePaths.has(path),
      )
      .sort((a, b) => a.localeCompare(b))
      .map(path => {
        const originalContent = isMarkdownSkillFilePath(path)
          ? this.getReviewOriginalMarkdown(path)
          : this._originalContentByPath.get(path);

        return {
          key: `modified:${path}`,
          changeType: "modified" as const,
          path,
          originalContent,
          currentContent: this._contentByPath.get(path) ?? "",
          requiresOriginalFetch: originalContent === undefined,
        };
      });
  }

  @computed private get deletedReviewItems(): SkillCollectionReviewItem[] {
    return [...this.deletedFilePaths]
      .sort((a, b) => a.localeCompare(b))
      .map(path => ({
        key: `deleted:${path}`,
        changeType: "deleted" as const,
        path,
        oldPath: path,
        requiresOriginalFetch: true,
      }));
  }

  @computed private get addedFolderReviewItems(): SkillCollectionReviewItem[] {
    return this.emptyFolderPaths
      .filter(path => !this._serverFolderPaths.has(path))
      .sort((a, b) => a.localeCompare(b))
      .map(path => ({
        key: `added-folder:${path}`,
        changeType: "added" as const,
        path: `${path}/`,
        requiresOriginalFetch: false,
        isEmptyFolder: true,
      }));
  }

  @computed get reviewItems(): SkillCollectionReviewItem[] {
    return [
      ...this.addedReviewItems,
      ...this.renamedReviewItems,
      ...this.modifiedReviewItems,
      ...this.deletedReviewItems,
      ...this.addedFolderReviewItems,
    ];
  }

  @action setSelectedPath(path: string | null) {
    this._selectedPath = path;
  }

  @action updateContent(path: string, content: string) {
    this._contentByPath.set(path, content);
  }

  @action loadInitialContent(path: string, content: string) {
    if (isMarkdownSkillFilePath(path)) {
      if (!this._editorDocByPath.has(path)) {
        this.setMarkdownContent(path, content);
      }
      const isPersistedServerFile =
        this._serverFilePaths.has(path) && !this._addedFilePaths.has(path);
      if (isPersistedServerFile && !this._originalContentByPath.has(path)) {
        this._originalContentByPath.set(path, content);
      }
      if (!this._originalDocSnapshotByPath.has(path)) {
        this.setOriginalMarkdownSnapshot(path);
      }
      return;
    }

    if (!this._contentByPath.has(path)) {
      this._contentByPath.set(path, content);
    }

    if (!this._originalContentByPath.has(path)) {
      this._originalContentByPath.set(path, content);
    }
  }

  @action updateDocumentJson(path: string, doc: SkillDocumentJson) {
    if (this._importProtectedPaths.has(path)) {
      this._importProtectedPaths.delete(path);
      return;
    }
    const serialized = SkillCollectionEditorStore.serializeJson(doc);
    const current = this._editorDocByPath.get(path);
    if (current && SkillCollectionEditorStore.serializeJson(current) === serialized) {
      return;
    }
    this._editorDocByPath.set(path, doc);
    this._contentByPath.set(path, documentJsonToMarkdown(doc));
  }

  @action snapshotEditorBaseline(path: string, doc: SkillDocumentJson) {
    this._editorDocByPath.set(path, doc);
    if (!this._editorBaselinedPaths.has(path)) {
      this._editorBaselinedPaths.add(path);
      this._originalDocSnapshotByPath.set(path, SkillCollectionEditorStore.serializeJson(doc));
    }
  }

  @action createFile(relativePath: string, options?: { initialContent?: string }) {
    const normalizedPath = normalizeRelativePath(relativePath);
    if (!normalizedPath) {
      return;
    }

    if (this._filePaths.has(normalizedPath)) {
      return;
    }

    this._filePaths.add(normalizedPath);

    const parentPath = getParentPath(normalizedPath);
    if (parentPath) {
      this._addedFolderPaths.add(parentPath);
    }

    this._addedFilePaths.add(normalizedPath);

    if (!this._contentByPath.has(normalizedPath)) {
      const initial = options?.initialContent ?? "";
      if (isMarkdownSkillFilePath(normalizedPath)) {
        this.setMarkdownContent(normalizedPath, initial);
      } else {
        this._contentByPath.set(normalizedPath, initial);
      }
    }

    this._selectedPath = normalizedPath;
  }

  @action createFolder(relativePath: string) {
    const normalizedPath = normalizeRelativePath(relativePath);
    if (!normalizedPath) {
      return;
    }

    this._addedFolderPaths.add(normalizedPath);
  }

  @action renameNode(path: string, type: "file" | "folder", value: string) {
    const nextPath = buildPathWithinParent(getParentPath(path) || null, value);

    if (nextPath === path) {
      return;
    }

    if (type === "file") {
      this.moveSingleFile(path, nextPath);
      return;
    }

    this.moveFolderNode(path, nextPath);
  }

  @action moveNode(
    sourcePath: string,
    sourceType: "file" | "folder",
    targetFolderPath: string | null,
  ) {
    const sourceName = sourcePath.split("/").filter(Boolean).pop();
    if (!sourceName) {
      return;
    }

    const nextBasePath = targetFolderPath
      ? normalizeRelativePath(`${targetFolderPath}/${sourceName}`)
      : sourceName;

    if (
      sourceType === "folder" &&
      (nextBasePath === sourcePath || nextBasePath.startsWith(`${sourcePath}/`))
    ) {
      return;
    }

    if (sourceType === "file") {
      if (this._filePaths.has(nextBasePath) || this.allFolderPathSet.has(nextBasePath)) {
        return;
      }

      this.moveSingleFile(sourcePath, nextBasePath);
      return;
    }

    this.moveFolderNode(sourcePath, nextBasePath);
  }

  @action deleteNode(path: string, type: "file" | "folder") {
    if (type === "file") {
      this.clearFileData(path);
      this._renamedPaths.delete(path);

      if (this._serverFilePaths.has(path)) {
        this._deletedPaths.add(path);
      }

      if (this._selectedPath === path) {
        this._selectedPath = null;
      }

      return;
    }

    const prefix = `${path}/`;
    const pathsToDelete = [...this._filePaths].filter(p => p === path || p.startsWith(prefix));
    const foldersToRemove = [...this._addedFolderPaths].filter(
      p => p === path || p.startsWith(prefix),
    );

    pathsToDelete.forEach(filePath => this.clearFileData(filePath));
    foldersToRemove.forEach(folderPath => this._addedFolderPaths.delete(folderPath));

    const hasPersistedFiles = pathsToDelete.some(filePath => this._serverFilePaths.has(filePath));
    if (hasPersistedFiles) {
      this._deletedPaths.add(path);
    }

    if (
      this._selectedPath &&
      (this._selectedPath === path || this._selectedPath.startsWith(`${path}/`))
    ) {
      this._selectedPath = null;
    }
  }

  getCreateError(parentPath: string | null, itemType: "file" | "folder", value: string) {
    const nameError = getTreeItemNameError(value, itemType);
    if (nameError) {
      return nameError;
    }

    const nextPath = buildPathWithinParent(parentPath, value);
    if (this._filePaths.has(nextPath) || this.allFolderPathSet.has(nextPath)) {
      return `An item named "${normalizeTreeItemName(value)}" already exists here.`;
    }

    return null;
  }

  getRenameError(path: string, itemType: "file" | "folder", value: string) {
    const nameError = getTreeItemNameError(value, itemType);
    if (nameError) {
      return nameError;
    }

    const nextPath = buildPathWithinParent(getParentPath(path) || null, value);
    if (
      nextPath !== path &&
      (this._filePaths.has(nextPath) || this.allFolderPathSet.has(nextPath))
    ) {
      return `An item named "${normalizeTreeItemName(value)}" already exists here.`;
    }

    return null;
  }

  @action syncPersistedTree(
    filePaths: string[],
    folderPaths: string[],
    fileContents?: Record<string, string>,
  ) {
    const samePersistedFiles = this.arePathSetsEqual(this._serverFilePaths, filePaths);
    const samePersistedFolders = this.arePathSetsEqual(this._serverFolderPaths, folderPaths);

    if (samePersistedFiles && samePersistedFolders) {
      return;
    }

    this._serverFilePaths = new Set(filePaths);
    this._serverFolderPaths = new Set(folderPaths);

    if (fileContents) {
      for (const [path, content] of Object.entries(fileContents)) {
        this.loadInitialContent(path, content);
      }
    }

    if (this.hasUnsavedStructureChanges) {
      return;
    }

    this._filePaths = new Set(filePaths);
    this._addedFolderPaths = new Set(folderPaths);
    this._addedFilePaths = new Set();
    this._renamedPaths = new Map();
    this._deletedPaths = new Set();
  }

  @action importLocalFiles(entries: { path: string; content: string }[]): string | null {
    const normalized = this.normalizeImportEntries(entries);
    if (normalized.length === 0) {
      return null;
    }

    this.prepareImport(normalized.map(item => item.path));
    normalized.forEach(({ path, content }) => this.importSingleFile(path, content));

    const sortedPaths = normalized.map(item => item.path).sort((a, b) => a.localeCompare(b));
    const firstPath = sortedPaths[0] ?? null;

    if (firstPath) {
      this._selectedPath = firstPath;
    }

    this._hasImportedContent = true;
    return firstPath;
  }

  async importLocalFilesAsync(
    entries: { path: string; content: string }[],
    onProgress?: (current: number, total: number) => void,
  ): Promise<string | null> {
    const normalized = this.normalizeImportEntries(entries);
    if (normalized.length === 0) {
      return null;
    }

    runInAction(() => {
      this.prepareImport(normalized.map(item => item.path));
    });

    const BATCH_SIZE = 20;
    for (let i = 0; i < normalized.length; i += BATCH_SIZE) {
      const batch = normalized.slice(i, i + BATCH_SIZE);
      runInAction(() => {
        for (const { path, content } of batch) {
          this.importSingleFile(path, content);
        }
      });
      const processed = Math.min(i + BATCH_SIZE, normalized.length);
      onProgress?.(processed, normalized.length);
      await new Promise<void>(resolve => {
        setTimeout(resolve, 0);
      });
    }

    const sortedPaths = normalized.map(item => item.path).sort((a, b) => a.localeCompare(b));
    const firstPath = sortedPaths[0] ?? null;

    runInAction(() => {
      if (firstPath) {
        this._selectedPath = firstPath;
      }
      this._hasImportedContent = true;
    });

    return firstPath;
  }

  private normalizeImportEntries(entries: { path: string; content: string }[]) {
    return entries.reduce<{ path: string; content: string }[]>((result, { path, content }) => {
      const normalizedPath = normalizeRelativePath(path);
      if (normalizedPath) {
        result.push({ path: normalizedPath, content });
      }
      return result;
    }, []);
  }

  @action private prepareImport(paths: string[]) {
    this._importProtectedPaths = new Set(paths.filter(path => this._filePaths.has(path)));
    this.clearDeletesAffectedByImport(paths);
  }

  @action private importSingleFile(path: string, content: string) {
    const parentPath = getParentPath(path);
    if (parentPath) {
      const parts = parentPath.split("/").filter(Boolean);
      for (let i = 0; i < parts.length; i += 1) {
        this._addedFolderPaths.add(parts.slice(0, i + 1).join("/"));
      }
    }

    const alreadyExists = this._filePaths.has(path);
    this._filePaths.add(path);

    if (!this._serverFilePaths.has(path)) {
      this._addedFilePaths.add(path);
    }

    if (alreadyExists) {
      this._originalContentByPath.delete(path);
      this._originalDocSnapshotByPath.delete(path);
      this._editorBaselinedPaths.delete(path);
      this._editorDocByPath.delete(path);
    }

    if (isMarkdownSkillFilePath(path)) {
      this.setMarkdownContent(path, content);
    } else {
      this._contentByPath.set(path, content);
    }
  }

  @action applyPresetFiles(presetFiles: PresetFile[], rootPath?: string | null): string | null {
    if (presetFiles.length === 0) {
      return null;
    }

    const normalizedPresetFiles = normalizePresetFiles(presetFiles, rootPath);
    const paths = normalizedPresetFiles.map(file => file.path).filter(Boolean);
    const firstPath = paths[0] ?? null;

    paths.forEach(path => this._filePaths.add(path));
    paths.forEach(path => this._addedFilePaths.add(path));

    normalizedPresetFiles.forEach(file => {
      if (!file.path) {
        return;
      }

      if (isMarkdownSkillFilePath(file.path)) {
        this.setMarkdownContent(file.path, file.content);
        this.setOriginalMarkdownSnapshot(file.path);
      } else {
        this._contentByPath.set(file.path, file.content);
        this._originalContentByPath.set(file.path, file.content);
      }

      const parentPath = getParentPath(file.path);
      if (parentPath) {
        this._addedFolderPaths.add(parentPath);
      }
    });

    this._selectedPath = firstPath;
    return firstPath;
  }

  @action markSaveSuccess() {
    for (const path of this.dirtyFilePaths) {
      if (isMarkdownSkillFilePath(path)) {
        this.setOriginalMarkdownSnapshot(path);
      }
      const current = this._contentByPath.get(path) ?? "";
      this._originalContentByPath.set(path, current);
    }

    this._addedFilePaths = new Set();
    this._renamedPaths = new Map();
    this._deletedPaths = new Set();
  }

  private static stripTrailingEmptyParagraphs(
    bodyJson: SkillDocumentJson["bodyJson"],
  ): SkillDocumentJson["bodyJson"] {
    const { content } = bodyJson;
    if (!content || content.length === 0) {
      return bodyJson;
    }

    let end = content.length;
    while (end > 0) {
      const node = content[end - 1];
      if (node.type !== "paragraph" || (node.content && node.content.length > 0)) {
        break;
      }
      end -= 1;
    }

    if (end === content.length) {
      return bodyJson;
    }

    return { ...bodyJson, content: content.slice(0, end) };
  }

  private static serializeJson(doc: SkillDocumentJson): string {
    const normalizedBody = SkillCollectionEditorStore.stripTrailingEmptyParagraphs(doc.bodyJson);
    return JSON.stringify({ f: doc.frontmatter, b: normalizedBody }, (_key, value) => {
      if (value && typeof value === "object" && !Array.isArray(value)) {
        return Object.fromEntries(Object.entries(value).sort(([a], [b]) => a.localeCompare(b)));
      }
      return value;
    });
  }

  @action private setMarkdownContent(path: string, markdown: string) {
    this._contentByPath.set(path, markdown);
    const docJson = markdownToDocumentJson(markdown);
    this._editorDocByPath.set(path, docJson);
  }

  @action private setOriginalMarkdownSnapshot(path: string) {
    const docJson = this._editorDocByPath.get(path);
    if (docJson) {
      this._originalDocSnapshotByPath.set(path, SkillCollectionEditorStore.serializeJson(docJson));
    }
  }

  private getReviewOriginalMarkdown(path: string): string | undefined {
    return this._originalContentByPath.get(path);
  }

  private isMarkdownDirty(path: string): boolean {
    const currentDoc = this._editorDocByPath.get(path);
    const originalSerialized = this._originalDocSnapshotByPath.get(path);
    if (!currentDoc || originalSerialized === undefined) {
      return true;
    }
    return SkillCollectionEditorStore.serializeJson(currentDoc) !== originalSerialized;
  }

  private isPathDeleted(path: string) {
    for (const deletedPath of this._deletedPaths) {
      if (deletedPath === path || path.startsWith(`${deletedPath}/`)) {
        return true;
      }
    }
    return false;
  }

  /**
   * After moving all files out of a folder, drop matching entries from the
   * persisted-folder set so the tree does not show an empty "ghost" branch.
   * (We do not add these paths to `_deletedPaths`: the API applies deletes
   * before moves, which would break moves from paths under that folder.)
   */
  @action private pruneRelocatedSourceFolderFromServerTree(sourcePath: string) {
    const normalized = normalizeRelativePath(sourcePath);
    if (!normalized) {
      return;
    }

    const prefix = `${normalized}/`;
    const stillHasFilesHere = [...this._filePaths].some(
      path => path === normalized || path.startsWith(prefix),
    );
    if (stillHasFilesHere) {
      return;
    }

    [...this._serverFolderPaths]
      .filter(path => path === normalized || path.startsWith(prefix))
      .forEach(path => this._serverFolderPaths.delete(path));
  }

  private arePathSetsEqual(current: Set<string>, nextValues: string[]) {
    if (current.size !== nextValues.length) {
      return false;
    }

    return nextValues.every(value => current.has(value));
  }

  @action private clearFileData(filePath: string) {
    this._filePaths.delete(filePath);
    this._contentByPath.delete(filePath);
    this._originalContentByPath.delete(filePath);
    this._editorDocByPath.delete(filePath);
    this._originalDocSnapshotByPath.delete(filePath);
    this._editorBaselinedPaths.delete(filePath);
    this._addedFilePaths.delete(filePath);

    const sourceForTarget = this.movedTargetToSource.get(filePath);
    if (sourceForTarget !== undefined) {
      this._renamedPaths.delete(sourceForTarget);
      if (this._serverFilePaths.has(sourceForTarget)) {
        this._deletedPaths.add(sourceForTarget);
      }
    }
  }

  @action private clearDeletesAffectedByImport(paths: string[]) {
    if (paths.length === 0) {
      return;
    }

    const toRemove = new Set<string>();

    paths.forEach(path => {
      this._deletedPaths.forEach(del => {
        if (path === del || path.startsWith(`${del}/`) || del.startsWith(`${path}/`)) {
          toRemove.add(del);
        }
      });
    });

    toRemove.forEach(del => this._deletedPaths.delete(del));
  }

  @action private renameContentKey(oldPath: string, newPath: string) {
    const content = this._contentByPath.get(oldPath);
    if (content !== undefined) {
      this._contentByPath.delete(oldPath);
      this._contentByPath.set(newPath, content);
    }

    const originalContent = this._originalContentByPath.get(oldPath);
    if (originalContent !== undefined) {
      this._originalContentByPath.delete(oldPath);
      this._originalContentByPath.set(newPath, originalContent);
    }

    const doc = this._editorDocByPath.get(oldPath);
    if (doc !== undefined) {
      this._editorDocByPath.delete(oldPath);
      this._editorDocByPath.set(newPath, doc);
    }

    const serialized = this._originalDocSnapshotByPath.get(oldPath);
    if (serialized !== undefined) {
      this._originalDocSnapshotByPath.delete(oldPath);
      this._originalDocSnapshotByPath.set(newPath, serialized);
    }

    if (this._editorBaselinedPaths.has(oldPath)) {
      this._editorBaselinedPaths.delete(oldPath);
      this._editorBaselinedPaths.add(newPath);
    }
  }

  @action private moveSingleFile(oldPath: string, newPath: string) {
    if (oldPath === newPath) {
      return;
    }

    const wasNewFile = this._addedFilePaths.has(oldPath);

    this._filePaths.delete(oldPath);
    this._filePaths.add(newPath);

    const parentPath = getParentPath(newPath);
    if (parentPath) {
      this._addedFolderPaths.add(parentPath);
    }

    if (wasNewFile) {
      this._addedFilePaths.delete(oldPath);
      this._addedFilePaths.add(newPath);
    }

    const originalSource = this.movedTargetToSource.get(oldPath);
    const sourcePath = originalSource ?? oldPath;

    if (originalSource !== undefined) {
      this._renamedPaths.delete(originalSource);
    }

    if (!wasNewFile && !this._serverFilePaths.has(sourcePath)) {
      this.renameContentKey(oldPath, newPath);
      if (this._selectedPath === oldPath) {
        this._selectedPath = newPath;
      }
      return;
    }

    if (!wasNewFile && sourcePath !== newPath) {
      this._renamedPaths.set(sourcePath, newPath);
    }

    this.renameContentKey(oldPath, newPath);

    if (this._selectedPath === oldPath) {
      this._selectedPath = newPath;
    }
  }

  @action private moveFolderNode(sourcePath: string, nextBasePath: string) {
    if (
      nextBasePath === sourcePath ||
      nextBasePath.startsWith(`${sourcePath}/`) ||
      this._filePaths.has(nextBasePath) ||
      (this.allFolderPathSet.has(nextBasePath) && nextBasePath !== sourcePath)
    ) {
      return;
    }

    const sourcePrefix = `${sourcePath}/`;
    const folderDescendants = [...this._filePaths]
      .filter(path => path.startsWith(sourcePrefix))
      .sort((a, b) => getPathDepth(a) - getPathDepth(b));

    const folderPathsToMove = [...this._addedFolderPaths]
      .filter(path => path === sourcePath || path.startsWith(sourcePrefix))
      .sort((a, b) => getPathDepth(a) - getPathDepth(b));

    folderPathsToMove.forEach(path => this._addedFolderPaths.delete(path));
    folderPathsToMove.forEach(path => {
      const suffix = path === sourcePath ? "" : path.slice(sourcePath.length + 1);
      const movedPath = suffix ? `${nextBasePath}/${suffix}` : nextBasePath;
      this._addedFolderPaths.add(movedPath);
    });

    folderDescendants.forEach(path => {
      const suffix = path.slice(sourcePath.length + 1);
      this.moveSingleFile(path, `${nextBasePath}/${suffix}`);
    });

    this.pruneRelocatedSourceFolderFromServerTree(sourcePath);

    if (this._selectedPath === sourcePath) {
      this._selectedPath = nextBasePath;
    }
  }
}
